import { computed, ref, watch } from 'vue'
import { defineStore } from 'pinia'

export type ThemeMode = 'light' | 'dark' | 'system'
export type ResolvedTheme = 'light' | 'dark'

export type ToastVariant = 'info' | 'success' | 'warning' | 'danger'

export interface Toast {
  id: string
  title: string
  description?: string
  variant: ToastVariant
  /** Milliseconds until auto-dismiss; 0 keeps it until dismissed. */
  timeout: number
  action?: { label: string; run: () => void }
}

export type ToastInput = Omit<Partial<Toast>, 'id'> & { title: string }

const THEME_KEY = 'eua.theme'
const SIDEBAR_KEY = 'eua.sidebar'

function readStored<T extends string>(key: string, allowed: readonly T[], fallback: T): T {
  try {
    const value = localStorage.getItem(key)
    return allowed.includes(value as T) ? (value as T) : fallback
  } catch {
    return fallback
  }
}

export const useUiStore = defineStore('ui', () => {
  const theme = ref<ThemeMode>(
    readStored(THEME_KEY, ['light', 'dark', 'system'] as const, 'system'),
  )
  const systemPrefersDark = ref(false)
  const sidebarCollapsed = ref(readStored(SIDEBAR_KEY, ['0', '1'] as const, '0') === '1')
  const mobileNavOpen = ref(false)
  const commandPaletteOpen = ref(false)
  const helpOpen = ref(false)
  const toasts = ref<Toast[]>([])

  const resolvedTheme = computed<ResolvedTheme>(() =>
    theme.value === 'system' ? (systemPrefersDark.value ? 'dark' : 'light') : theme.value,
  )

  /** Called once from `main.ts`; keeps `<html data-theme>` in sync. */
  function initTheme(): void {
    const query = window.matchMedia('(prefers-color-scheme: dark)')
    systemPrefersDark.value = query.matches
    query.addEventListener('change', (event) => {
      systemPrefersDark.value = event.matches
    })
    watch(resolvedTheme, (value) => document.documentElement.setAttribute('data-theme', value), {
      immediate: true,
    })
  }

  function setTheme(mode: ThemeMode): void {
    theme.value = mode
    try {
      // `system` is stored as an absent key so index.html's bootstrap falls through.
      if (mode === 'system') localStorage.removeItem(THEME_KEY)
      else localStorage.setItem(THEME_KEY, mode)
    } catch {
      /* private mode — the theme just will not persist */
    }
  }

  function cycleTheme(): void {
    setTheme(resolvedTheme.value === 'dark' ? 'light' : 'dark')
  }

  function setSidebarCollapsed(value: boolean): void {
    sidebarCollapsed.value = value
    try {
      localStorage.setItem(SIDEBAR_KEY, value ? '1' : '0')
    } catch {
      /* ignore */
    }
  }

  function toggleSidebar(): void {
    setSidebarCollapsed(!sidebarCollapsed.value)
  }

  function openCommandPalette(): void {
    commandPaletteOpen.value = true
  }

  function toggleCommandPalette(): void {
    commandPaletteOpen.value = !commandPaletteOpen.value
  }

  function openHelp(): void {
    helpOpen.value = true
  }

  function dismissToast(id: string): void {
    toasts.value = toasts.value.filter((toast) => toast.id !== id)
  }

  function pushToast(input: ToastInput): string {
    const id = crypto.randomUUID()
    const toast: Toast = {
      id,
      title: input.title,
      variant: input.variant ?? 'info',
      timeout: input.timeout ?? (input.variant === 'danger' ? 8000 : 4500),
      ...(input.description ? { description: input.description } : {}),
      ...(input.action ? { action: input.action } : {}),
    }
    toasts.value = [...toasts.value, toast]
    if (toast.timeout > 0) window.setTimeout(() => dismissToast(id), toast.timeout)
    return id
  }

  function clearToasts(): void {
    toasts.value = []
  }

  return {
    theme,
    resolvedTheme,
    systemPrefersDark,
    sidebarCollapsed,
    mobileNavOpen,
    commandPaletteOpen,
    helpOpen,
    toasts,
    initTheme,
    setTheme,
    cycleTheme,
    setSidebarCollapsed,
    toggleSidebar,
    openCommandPalette,
    toggleCommandPalette,
    openHelp,
    pushToast,
    dismissToast,
    clearToasts,
  }
})
